import * as constants from '../../constants';
import { DEFAULT_STORE_AVAILABILITY, EMPTY_STORE } from '../../constants';
import { content, icons } from '../../content';
import * as cookie from '../../storage/cookie';
import * as storage from '../../storage/storage';
import * as storesApi from './storesApi';
import NearestStoreMenu from './NearestStoreMenu';
import { ANALYTICS_CONSTANTS, track } from '../../utils/analytics';
import { IPayload } from '../../types/analytics';
import { Store, StoreAvailability } from '../../types/store';

interface ISubscribeToStoreUpdatesEventDetail {
    done: (store: Store) => any;
}
export type SubscribeToStoreUpdatesEventDetail = ISubscribeToStoreUpdatesEventDetail;

interface IOpenStoreSelectionModal {
    done?: () => any;
    stateOrZip: string|number;
}
export type OpenStoreSelectionModalEventDetail = IOpenStoreSelectionModal;

interface ISetStoreEventDetail {
    done?: () => any;
    store: Store;
}
export type SetStoreEventDetail = ISetStoreEventDetail;

interface ISetStoreByIdEventDetail {
    distanceInMiles: number;
    done?: (store: Store) => any;
    id: Store['Id'];
    reload?: boolean;
}
export type SetStoreByIdEventDetail = ISetStoreByIdEventDetail;

export interface INearestStore {
    headerMenu: NearestStoreMenu;
    footerMenu: NearestStoreMenu;
    nearestStore: Store;
    initialRenderingComplete: boolean;
    flyoutOpen: { [index: string]: boolean };
    footerFlyoutOpen: boolean;
    storeUpdateSubscribers: SubscribeToStoreUpdatesEventDetail['done'][];
    init(): void;
    attachSubscribeToStoreUpdatesEventListener(): void;
    attachSetStoreEventListener(): void;
    attachSetStoreByIdEventListener(): void;
    attachSetStoreByIdOnloadEventListener():void;
    attachOpenStoreSelectionModalListener(): void;
    handleLoad(store: Store): void;
    load(): void;
    toggle(isTarget: boolean, type: 'header' | 'footer'): void;
    handleButtonKeydown(event: KeyboardEvent, string: 'header' | 'footer'): void;
    handleMenuKeydown(event: KeyboardEvent, string: 'header' | 'footer'): void;
    handleMouseover(): void;
    handleClickOutside(type: 'header' | 'footer'): void;
    renderHeaderStoreName(): void;
    renderFooterStoreName(): void;
    renderHeaderStoreAlerts(hasAlert: boolean): void;
    renderFooterStoreAlerts(hasAlert: boolean): void;
    renderAll(force: boolean): void;
    update(store: Store): void;
}

export default class NearestStore implements INearestStore {
    private _nearestStore: Store;
    private _storeAvailability: StoreAvailability;
    headerMenu: NearestStoreMenu;
    footerMenu: NearestStoreMenu;
    initialRenderingComplete: boolean;
    flyoutOpen: { [index: string]: boolean };
    footerFlyoutOpen: boolean;
    storeUpdateSubscribers: SubscribeToStoreUpdatesEventDetail['done'][];
    menuLocation: string;

    constructor() {
        this._nearestStore = EMPTY_STORE;
        this._storeAvailability = DEFAULT_STORE_AVAILABILITY;
        this.initialRenderingComplete = false;
        this.flyoutOpen = { header: false, footer: false };
        this.footerFlyoutOpen = false;
        this.storeUpdateSubscribers = [];
        this.menuLocation = '';

        this.attachSubscribeToStoreUpdatesEventListener();
        this.attachSetStoreEventListener();
        this.attachSetStoreByIdEventListener();
        this.attachSetStoreByIdOnloadEventListener();
        this.attachOpenStoreSelectionModalListener();

        this.headerMenu = new NearestStoreMenu('header', this);
        this.footerMenu = new NearestStoreMenu('footer', this);
    }

    public init(): void {
        this.load();
    }

    get nearestStore(): Store {
        return this._nearestStore;
    }

    set nearestStore(store: Store) {
        this._nearestStore = store;

        this.storeUpdateSubscribers.forEach((subscriber: SubscribeToStoreUpdatesEventDetail['done']) => {
            subscriber(store);
        });
    }

    get storeAvailability(): StoreAvailability {
        return this._storeAvailability;
    }

    set storeAvailability(storeAvailability: StoreAvailability) {
        this._storeAvailability = storeAvailability;
    }

    public attachSubscribeToStoreUpdatesEventListener(): void {
        document.documentElement.addEventListener(constants.SUBSCRIBE_TO_STORE_UPDATES_CUSTOM_EVENT, ((
            event: CustomEvent
        ) => {
            const eventDetail: SubscribeToStoreUpdatesEventDetail = event.detail;

            if (eventDetail.done && typeof eventDetail.done === 'function') {
                if (this.nearestStore.Id) {
                    eventDetail.done(this.nearestStore);
                }
                this.storeUpdateSubscribers.push(eventDetail.done);
            }
        }) as EventListener);
    }

    public attachSetStoreEventListener(): void {
        document.documentElement.addEventListener(constants.SET_STORE_CUSTOM_EVENT, ((event: CustomEvent) => {
            const eventDetail: SetStoreEventDetail = event.detail;
            this.update(eventDetail.store);
            if (eventDetail.done && typeof eventDetail.done === 'function') {
                eventDetail.done();
            }
        }) as EventListener);
    }

    public attachOpenStoreSelectionModalListener(): void {
        document.documentElement.addEventListener(
            constants.OPEN_STORE_SELECTION_MODAL_EVENT, ((event: CustomEvent) => {
            const eventDetail: OpenStoreSelectionModalEventDetail = event.detail;

            const customEvent = new CustomEvent(content.STORE_CHOOSER.EVENT_NAMES.headerKeywordInput, { detail: { keyword: eventDetail.stateOrZip }});
            dispatchEvent(customEvent);
        }) as EventListener);
    }

    public attachSetStoreByIdEventListener(): void {
        document.documentElement.addEventListener(constants.SET_STORE_BY_ID_CUSTOM_EVENT, ((event: CustomEvent) => {
            this.setStoreByID(true,event)

        }) as EventListener);
    }

    public setStoreByID(sendDataToAdobeAnalytics:boolean, event: CustomEvent):void {

            const eventDetail: SetStoreByIdEventDetail = event.detail;
            const currentStore = cookie.getStoreId();



            if(eventDetail.id && currentStore !== eventDetail.id) {

                storesApi.getStore(eventDetail.id, (store: Store) => {
                    if (store && store.Id) {
                        cookie.setStoreCookie(store);
                        store.DistanceInMiles = eventDetail.distanceInMiles ? eventDetail.distanceInMiles : 0;
                        this.update(store);
                    }
                    if (eventDetail.done && typeof eventDetail.done === 'function') {
                        eventDetail.done(store);
                    }
                });
            }
    }

    /**
     * @description set store onload without sending data to adobe analytics
     */
    public attachSetStoreByIdOnloadEventListener(): void {
        document.documentElement.addEventListener(constants.SET_STORE_BY_ID_ONLOAD_CUSTOM_EVENT, ((event: CustomEvent) => {
            this.setStoreByID(false,event)
        }) as EventListener);
    }

    public handleLoad(store: Store): void {
        this.nearestStore = store;

        this.renderHeaderStoreName();
        this.renderFooterStoreName();
        this.renderHeaderStoreAlerts(!!this.nearestStore.AlertMessage);
        this.renderFooterStoreAlerts(!!this.nearestStore.AlertMessage);

        if (this.initialRenderingComplete) {
            this.renderAll(true);
        }
    }

    public load(): void {
        let nearestStoreId = cookie.getStoreId();

        function validateCachedStoreExists(cStore: Store): boolean {
            return cStore && cStore.Id && cStore.Hours ? true : false;
        }

        if (!nearestStoreId) {
            this.handleLoad(EMPTY_STORE);
        } else {
            let cachedStore: Store | null = storage.getStore(nearestStoreId);

            if (cachedStore && validateCachedStoreExists(cachedStore)) {
                this.handleLoad(cachedStore);
            } else {
                storesApi.getStore(nearestStoreId, (store: Store) => {
                    storage.setStore(store);
                    this.handleLoad(store);
                });
            }

            // Ideally this would be combined with getStore request
            // But the store data caching used both here and in the stores api would make it difficult.
            storesApi.getStoreAvailability(nearestStoreId, (availability: StoreAvailability) => {
                this.storeAvailability = availability;
                this.handleLoad(this.nearestStore);
            });
        }
    }

    public toggle(isTarget: boolean, type: string): void {
        this.menuLocation = type;
        if (isTarget) {
            if (!this.initialRenderingComplete) {
                this.renderAll();
                this.initialRenderingComplete = true;
            }
            type === 'header' ? this.headerMenu.handleOpenButtonClick() : this.footerMenu.handleOpenButtonClick();
        }

        function closeStoreMenuOnInputSubmit(menuToClose: NearestStoreMenu) {
            menuToClose.handleCloseButtonClick();
        }

        const activeMenu: NearestStoreMenu = type === 'header' ? this.headerMenu : this.footerMenu;
        const searchHeaderInputForm: HTMLFormElement | null = document.querySelector(
            `#${content.HEADER_MY_STORE.storeSearchFormId}`
        );
        const searchFooterInputForm: HTMLFormElement | null = document.querySelector(
            `#${content.FOOTER_MY_STORE.storeSearchFormId}`
        );
        if (searchHeaderInputForm) {
            searchHeaderInputForm.addEventListener('submit', (e: Event) => {
                const formData = new FormData(searchHeaderInputForm);
                const keywordInput = formData.get('keyword');
                const keywordInputEvent = new CustomEvent(content.STORE_CHOOSER.EVENT_NAMES.headerKeywordInput, {
                    detail: { keyword: keywordInput }
                });
                dispatchEvent(keywordInputEvent);
                closeStoreMenuOnInputSubmit(activeMenu);
            });
        }
        if (searchFooterInputForm) {
            searchFooterInputForm.addEventListener('submit', (e: Event) => {
                const formData = new FormData(searchFooterInputForm);
                const keywordInput = formData.get('keyword');
                const keywordInputEvent = new CustomEvent(content.STORE_CHOOSER.EVENT_NAMES.headerKeywordInput, {
                    detail: { keyword: keywordInput }
                });
                dispatchEvent(keywordInputEvent);
                closeStoreMenuOnInputSubmit(activeMenu);
            });
        }
    }

    public handleButtonKeydown(event: KeyboardEvent, type: 'header' | 'footer'): void {
        type === 'header'
            ? this.headerMenu.handleOpenButtonKeydown(event)
            : this.footerMenu.handleOpenButtonKeydown(event);
    }

    public handleMenuKeydown(event: KeyboardEvent, type: 'header' | 'footer'): void {
        type === 'header' ? this.headerMenu.handleMenuKeydown(event) : this.footerMenu.handleMenuKeydown(event);
    }

    public handleMouseover(): void {
        if (!this.initialRenderingComplete) {
            this.renderAll();
            this.initialRenderingComplete = true;
        }
    }

    public handleClickOutside(type: 'header' | 'footer'): void {
        type === 'header' ? this.headerMenu.handleClickOutside() : this.footerMenu.handleClickOutside();
    }

    public renderHeaderStoreName(): void {
        let headerButtonEl: HTMLElement | null = document.querySelector(`#${content.HEADER_MY_STORE.buttonId}`);
        const zipCodeToDisplay = cookie.getUserEnteredZipCode() ?? this.nearestStore.ZipCode;

        // Inject new html so that other repositories do not have to rebuild/deploy html changes.
        if (headerButtonEl) {
            headerButtonEl.classList.add('visible');
            if (this.nearestStore.Id) {
                headerButtonEl.innerHTML = '';
                let buttonHtml = headerButtonEl.innerHTML;
                let locationIconHtml = `<svg class="header-footer-icon" width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 1.29004C6.64999 1.29004 2.28999 5.65004 2.28999 11C2.28999 16.35 8.47999 20.6 10.52 21.92C10.97 22.21 11.49 22.36 12 22.36C12.51 22.36 13.03 22.21 13.48 21.92C15.51 20.6 21.71 16.59 21.71 11C21.71 5.41004 17.35 1.29004 12 1.29004ZM12.71 20.73C12.28 21.01 11.72 21.01 11.29 20.73C9.41999 19.51 3.70999 15.81 3.70999 11C3.70999 6.19004 7.42999 2.71004 12 2.71004C16.57 2.71004 20.29 6.43004 20.29 11C20.29 15.82 14.58 19.52 12.71 20.73Z" fill="#001333"/>
                    <path d="M12 7.29004C9.94999 7.29004 8.28999 8.95004 8.28999 11C8.28999 13.05 9.94999 14.71 12 14.71C14.05 14.71 15.71 13.05 15.71 11C15.71 8.95004 14.05 7.29004 12 7.29004ZM12 13.29C10.74 13.29 9.70999 12.26 9.70999 11C9.70999 9.74004 10.74 8.71004 12 8.71004C13.26 8.71004 14.29 9.74004 14.29 11C14.29 12.26 13.26 13.29 12 13.29Z" fill="#001333"/>
                    <circle class="hidden header-footer-location-icon-alert-circle" cx="19" cy="4" r="4"
                        stroke="white" stroke-width="1" />
                </svg>`;
                let appendHtml = `<div class="header-footer-flyout-button-text-container">
                <span id="header-my-store-button-zipCode">Your store for ${zipCodeToDisplay}</span>
                    <div class="header-find-your-store-container">
                        <span id="header-my-store-button-text">${this.nearestStore.Name}</span>
                    </div>
                </div>`;
                headerButtonEl.innerHTML = locationIconHtml + buttonHtml + appendHtml;
            }
        } else {
            let headerMyStoreButton = <HTMLElement>(
                document.querySelector(
                    `#${content.HEADER.id} #${content.HEADER_MY_STORE.buttonId} > .${content.HEADER_MY_STORE.iconClass}`
                )
            );
            let headerMyStoreSection = <HTMLElement>(
                document.querySelector(`#${content.HEADER.id} .${content.HEADER_MY_STORE.sectionClass}`)
            );

            if (headerMyStoreButton) {
                headerMyStoreButton.classList.add(content.CLASSNAMES.visible);
            }
            if (headerMyStoreSection) {
                headerMyStoreSection.classList.add(content.HEADER_MY_STORE.sectionClassNoStore);
            }
        }
    }

    public renderFooterStoreName(): void {
        let footerFlyoutButtonTextEl = document.querySelector(
            `#${content.FOOTER.id} #${content.FOOTER_MY_STORE.buttonTextId}`
        );
        if (footerFlyoutButtonTextEl && this.nearestStore.Id) {
            footerFlyoutButtonTextEl.textContent = this.nearestStore.Name;
        }
    }

    private renderHeaderAlertInfoIcon(hasAlert: boolean): void {
        let existingStoreAlertContainerEl = <HTMLElement>(
            document.querySelector(`#${content.HEADER.id} .${content.HEADER_MY_STORE.alertInfoIconContainerClass}`)
        );

        if (hasAlert) {
            let headerMyStoreButtonEl = <HTMLElement>(
                document.querySelector(`#${content.HEADER.id} #${content.HEADER_MY_STORE.buttonId}`)
            );

            if (!existingStoreAlertContainerEl && headerMyStoreButtonEl) {
                let firstChildEl = headerMyStoreButtonEl.firstChild;
                let storeAlertContainerEl = document.createElement('span');
                storeAlertContainerEl.classList.add(content.HEADER_MY_STORE.alertInfoIconContainerClass);
                storeAlertContainerEl.innerHTML = icons.buildAlertInfoIcon(content.HEADER_MY_STORE.alertInfoIconClass);
                headerMyStoreButtonEl.insertBefore(storeAlertContainerEl, firstChildEl);
            }
        } else {
            if (existingStoreAlertContainerEl && existingStoreAlertContainerEl.parentNode) {
                existingStoreAlertContainerEl.parentNode.removeChild(existingStoreAlertContainerEl);
            }
        }
    }

    private renderAlertLocationIcon(options: { hasAlert: boolean; type: 'HEADER' | 'FOOTER' }): void {
        let myStoreConstantName = options.type === 'HEADER' ? 'HEADER_MY_STORE' : 'FOOTER_MY_STORE';
        let svgCircleAlertEls = document.querySelectorAll(
            `#${(content[options.type] as any).id} .${
                ((content as any)[myStoreConstantName] as any).locationAlertCircleClass
            }`
        );
        let myStoreButton = document.querySelector(`#${((content as any)[myStoreConstantName] as any).buttonId}`);

        if (myStoreButton) {
            if (options.hasAlert) {
                myStoreButton.classList.add(((content as any)[myStoreConstantName] as any).buttonAlertClass);

                if (svgCircleAlertEls && svgCircleAlertEls.length > 0) {
                    svgCircleAlertEls.forEach((el: Element) => {
                        el && el.classList && el.classList.remove(content.CLASSNAMES.hidden);
                    });
                }
            } else {
                myStoreButton.classList.remove(((content as any)[myStoreConstantName] as any).buttonAlertClass);

                if (svgCircleAlertEls.length > 0) {
                    svgCircleAlertEls.forEach((el: Element) => {
                        el && el.classList && el.classList.add(content.CLASSNAMES.hidden);
                    });
                }
            }
        }
    }

    public renderHeaderStoreAlerts(hasAlert: boolean): void {
        this.renderHeaderAlertInfoIcon(hasAlert);
        this.renderAlertLocationIcon({ hasAlert: hasAlert, type: 'HEADER' });
    }

    public renderFooterStoreAlerts(hasAlert: boolean): void {
        this.renderAlertLocationIcon({ hasAlert: hasAlert, type: 'FOOTER' });
    }

    public renderAll(force = false): void {
        this.renderHeaderStoreName();
        this.renderFooterStoreName();
        this.renderHeaderStoreAlerts(!!this.nearestStore.AlertMessage);
        this.renderFooterStoreAlerts(!!this.nearestStore.AlertMessage);

        this.headerMenu.render(force);
        this.footerMenu.render(force);

        if (!this.initialRenderingComplete) {
            this.initialRenderingComplete = true;
        }
    }

    public update(store: Store): void {
        this.nearestStore = store;
        storage.setStore(store);
        this.renderAll(true);

    }
}
